import React from 'react';
import { Link } from 'react-router-dom';

const PROD_BASE = 'https://api.shakespeareai.net';

const SHOWCASE_BOOKS = [
  { title: 'The Vanishing Protocol', cover: '/downloads/covers/cover_281_1773244371817.png', token: 'c2844e9324' },
  { title: 'The Phosphene Cartographer', cover: '/downloads/covers/cover_272_1773157428235.png', token: '4fd0e369e2' },
  { title: 'The Ember Throne', cover: '/downloads/covers/cover_174_1772506370245.png', token: 'e4fcb6a8f8' },
  { title: 'Thorns of Obsidian', cover: '/downloads/covers/cover_218_1772652154.png', token: '7d22127476' },
  { title: 'The Watcher on Finch Lane', cover: '/downloads/covers/cover_240_1772902125087.png', token: 'cc8f2bb2f3' },
  { title: 'The Gilded Bones of Firenze', cover: '/downloads/covers/cover_252_1772985114171.png', token: 'be4d866172' },
];

const proofBarGridStyle = `
  .proof-bar-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
    max-width: 960px;
    margin: 0 auto;
  }
  @media (max-width: 640px) {
    .proof-bar-grid {
      grid-template-columns: repeat(2, 1fr) !important;
    }
  }
`;

/* ── Proof Bar ── */
export const ProofBar: React.FC = () => (
  <section style={{
    padding: '48px 24px',
    background: 'var(--ground)',
  }}>
    <style>{proofBarGridStyle}</style>
    <div className="proof-bar-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' as any }}>
      {[
        { icon: '📚', value: '12,400+', label: 'Books written' },
        { icon: '✍️', value: '38M', label: 'Words generated' },
        { icon: '📖', value: '2,100+', label: 'Series authors' },
      ].map((item) => (
        <div key={item.label} style={{
          background: 'var(--surface)',
          border: '1px solid rgba(212,165,116,0.15)',
          borderRadius: '16px',
          padding: '28px 16px',
          textAlign: 'center' as const,
          display: 'flex',
          flexDirection: 'column' as const,
          alignItems: 'center',
          gap: '8px',
        }}>
          <div style={{ fontSize: '28px', lineHeight: 1 }}>{item.icon}</div>
          <div style={{ fontSize: '32px', fontWeight: 800, color: 'var(--accent)' }}>{item.value}</div>
          <div style={{ fontSize: '13px', color: 'var(--text-secondary)', letterSpacing: '0.5px' }}>{item.label}</div>
        </div>
      ))}
    </div>
  </section>
);

/* ── Three Pillars: Series Memory · AI Humanizer · Voice Engine ── */
const pillarsCss = `
  .pillars-grid { display: grid; grid-template-columns: 1fr; gap: 16px; max-width: 1100px; margin: 0 auto; }
  @media (min-width: 900px) { .pillars-grid { grid-template-columns: repeat(3, 1fr); } }
`;
export const ThreePillars: React.FC = () => {
  const pillars = [
    {
      icon: '📖',
      tag: 'Author+ feature',
      tagMuted: false,
      title: 'Series Memory',
      lead: 'One character bible. One world. Every book in your series stays consistent — because ShakespeareAI remembers what happened in book 1 when you write book 7.',
      bullets: ['Persistent character profiles across books', 'World-building rules never drift', 'Continuity auditor flags conflicts'],
    },
    {
      icon: '🛡️',
      tag: 'Author+ feature',
      tagMuted: false,
      title: 'AI Humanizer',
      lead: 'Your prose passes the detectors readers, publishers, and platforms care about. Built into the writing flow — not a separate tool you have to remember to run.',
      bullets: ['Turnitin & GPTZero compatible', 'Inline humanize toggle on every chapter', 'Bulk humanize entire manuscripts'],
    },
    {
      icon: '🎙️',
      tag: 'Pro Max feature',
      tagMuted: false,
      title: 'Voice Engine',
      lead: 'Upload your existing books. ShakespeareAI learns your sentence rhythm, vocabulary, and narrative voice — then writes book 8 the way you wrote book 1.',
      bullets: ['Train on your own backlist', 'Voice consistency score per chapter', 'Style match in 30+ languages'],
    },
  ];
  return (
    // id="series-memory" is the target of the hero's "See How Series Memory
    // Works" button, which used to point at #showcase — the gallery of books
    // other people have written. That answered a different question than the
    // one the button asks, and the first card here is literally Series Memory.
    // scroll-margin-top keeps the heading clear of the fixed 62px nav, which
    // would otherwise cover it on arrival.
    <section
      id="series-memory"
      style={{ padding: '64px 24px', background: 'var(--t-0c0907)', scrollMarginTop: '76px' }}
    >
      <style>{pillarsCss}</style>
      <div style={{ maxWidth: '1100px', margin: '0 auto 40px', textAlign: 'center' }}>
        <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.22em', color: 'var(--accent-alt)', marginBottom: '14px' }}>Three reasons to stay</div>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 500, color: 'var(--accent-soft)', lineHeight: 1.15, letterSpacing: '-0.02em', maxWidth: '720px', margin: '0 auto' }}>
          Other tools write a chapter. <em style={{ color: 'var(--accent-alt)', fontStyle: 'italic', fontWeight: 400 }}>ShakespeareAI builds a body of work.</em>
        </h2>
      </div>
      <div className="pillars-grid">
        {pillars.map(p => (
          <div key={p.title} style={{ background: 'linear-gradient(180deg, var(--t-1a140e), var(--t-14100b))', border: '1px solid var(--border)', borderRadius: '18px', padding: '30px 26px', position: 'relative', overflow: 'hidden' }}>
            <div style={{ width: '46px', height: '46px', borderRadius: '12px', background: 'linear-gradient(135deg, rgba(212,166,86,0.15), rgba(212,166,86,0.05))', border: '1px solid #3d2e1f', display: 'grid', placeItems: 'center', fontSize: '22px', marginBottom: '18px' }}>{p.icon}</div>
            <span style={{ display: 'inline-block', fontSize: '10px', fontWeight: 600, letterSpacing: '0.18em', textTransform: 'uppercase', color: p.tagMuted ? 'var(--text-muted)' : 'var(--accent-alt)', padding: '4px 10px', border: `1px solid ${p.tagMuted ? '#3d2e1f' : 'rgba(212,166,86,0.4)'}`, borderRadius: '999px', marginBottom: '14px' }}>{p.tag}</span>
            <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '22px', fontWeight: 500, color: 'var(--accent-soft)', margin: '0 0 10px', letterSpacing: '-0.015em' }}>{p.title}</h3>
            <p style={{ color: 'var(--t-d9cdb6)', fontSize: '14px', lineHeight: 1.6, margin: '0 0 18px' }}>{p.lead}</p>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {p.bullets.map(b => (
                <li key={b} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '13px', color: 'var(--text-muted)' }}>
                  <span style={{ color: 'var(--accent-alt)', fontWeight: 700, flex: '0 0 14px' }}>✓</span>{b}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
};

/* ── Comparison Table ── */
const comparisonCss = `
  .compare-grid { display: grid; grid-template-columns: 1.4fr 1fr 1fr 1fr; }
  @media (max-width: 700px) {
    .compare-grid { grid-template-columns: 1.6fr 0.8fr 0.8fr 0.8fr; }
    .compare-cell { padding: 12px 6px !important; font-size: 12px !important; }
    .compare-cell-head { font-size: 10px !important; }
  }
`;
export const ComparisonTable: React.FC = () => {
  const rows = [
    { feature: 'Series Memory across books', us: '✓', chatgpt: '—', sudowrite: '—' },
    { feature: 'Inline AI Humanizer',         us: '✓', chatgpt: '—', sudowrite: '—' },
    { feature: '30+ language translation',     us: '✓', chatgpt: '—', sudowrite: '—' },
    { feature: 'KDP-ready export',             us: '✓', chatgpt: '—', sudowrite: 'partial' },
    { feature: 'Generous free tier',           us: '✓', chatgpt: 'limited', sudowrite: 'trial only' },
  ];
  const cellStyle: React.CSSProperties = { padding: '16px 14px', fontSize: '14px', textAlign: 'center', borderBottom: '1px solid #2d2218' };
  const headStyle: React.CSSProperties = { padding: '18px 14px', fontSize: '12px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-muted)', textAlign: 'center', background: 'var(--t-14100b)', borderBottom: '1px solid #2d2218' };
  return (
    <section style={{ padding: '64px 24px', background: 'var(--t-0c0907)' }}>
      <style>{comparisonCss}</style>
      <div style={{ maxWidth: '960px', margin: '0 auto 32px', textAlign: 'center' }}>
        <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.22em', color: 'var(--accent-alt)', marginBottom: '12px' }}>Side by side</div>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(26px, 3.6vw, 38px)', fontWeight: 500, color: 'var(--accent-soft)', lineHeight: 1.2, letterSpacing: '-0.02em', margin: 0 }}>
          The features serious authors actually pay for.
        </h2>
      </div>
      <div style={{ maxWidth: '960px', margin: '0 auto', background: 'var(--t-1a140e)', border: '1px solid #2d2218', borderRadius: '16px', overflow: 'hidden' }}>
        <div className="compare-grid">
          <div className="compare-cell-head" style={{ ...headStyle, textAlign: 'left' }}>Feature</div>
          <div className="compare-cell-head" style={{ ...headStyle, color: 'var(--accent-alt)', background: 'linear-gradient(180deg, rgba(212,166,86,0.08), transparent)' }}>ShakespeareAI</div>
          <div className="compare-cell-head" style={headStyle}>ChatGPT</div>
          <div className="compare-cell-head" style={headStyle}>Sudowrite</div>
          {rows.map((r, i) => (
            <React.Fragment key={r.feature}>
              <div className="compare-cell" style={{ ...cellStyle, textAlign: 'left', color: 'var(--t-d9cdb6)', fontWeight: 500, borderBottom: i === rows.length - 1 ? 0 : cellStyle.borderBottom }}>{r.feature}</div>
              <div className="compare-cell" style={{ ...cellStyle, background: 'rgba(212,166,86,0.04)', color: 'var(--accent-alt)', fontWeight: 700, borderBottom: i === rows.length - 1 ? 0 : cellStyle.borderBottom }}>{r.us}</div>
              <div className="compare-cell" style={{ ...cellStyle, color: r.chatgpt === '—' ? 'var(--text-faint)' : 'var(--text-muted)', borderBottom: i === rows.length - 1 ? 0 : cellStyle.borderBottom }}>{r.chatgpt}</div>
              <div className="compare-cell" style={{ ...cellStyle, color: r.sudowrite === '—' ? 'var(--text-faint)' : 'var(--text-muted)', borderBottom: i === rows.length - 1 ? 0 : cellStyle.borderBottom }}>{r.sudowrite}</div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ── Final CTA Panel ── */
export const SeriesAuthorCTA: React.FC = () => (
  <section style={{ padding: '64px 24px 88px', background: 'var(--t-0c0907)' }}>
    <div className="series-cta-panel">
      <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(26px, 3.6vw, 40px)', fontWeight: 500, color: 'var(--accent-soft)', lineHeight: 1.2, letterSpacing: '-0.02em', margin: '0 0 14px' }}>
        Your series isn't a side project. <em style={{ color: 'var(--accent-alt)', fontStyle: 'italic', fontWeight: 400 }}>Treat it that way.</em>
      </h2>
      <p style={{ color: 'var(--text-muted)', fontSize: '16px', maxWidth: '520px', margin: '0 auto 26px' }}>
        Start free. Upgrade when your second book proves it works.
      </p>
      <Link to="/bookwriter" className="btn-primary">
        ✍️ Start writing free
      </Link>
    </div>
  </section>
);

/* ── Books Showcase (Carousel) ── */
export const BooksShowcase: React.FC = () => {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [paused, setPaused] = React.useState(false);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const id = setInterval(() => {
      if (paused) return;
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (maxScroll <= 0) return;
      if (el.scrollLeft >= maxScroll - 2) {
        el.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        el.scrollBy({ left: 200, behavior: 'smooth' });
      }
    }, 3500);
    return () => clearInterval(id);
  }, [paused]);

  const scroll = (dir: number) => {
    scrollRef.current?.scrollBy({ left: dir * 250, behavior: 'smooth' });
  };

  const arrowStyle: React.CSSProperties = {
    position: 'absolute', top: '50%', transform: 'translateY(-50%)', zIndex: 2,
    width: 36, height: 36, borderRadius: '50%', border: '1px solid rgba(212,165,116,0.3)',
    background: 'var(--r-1a1412-a850)', color: 'var(--accent)', fontSize: 20, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };

  return (
    <section style={{ padding: '64px 24px', maxWidth: 1060, margin: '0 auto' }}>
      <h2 style={{
        textAlign: 'center', fontSize: 'clamp(22px, 4vw, 34px)', fontWeight: 700,
        marginBottom: 12, color: 'var(--text-primary)'
      }}>
        Real Books. Published on Amazon. Made with ShakespeareAI.
      </h2>
      <p style={{ textAlign: 'center', fontSize: 16, color: 'var(--text-secondary)', marginBottom: 40 }}>
        These books were created entirely with our platform — covers, content, and all.
      </p>
      <div style={{ position: 'relative' }}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <button onClick={() => scroll(-1)} aria-label="Previous" style={{ ...arrowStyle, left: -16 }}>{'‹'}</button>
        <div ref={scrollRef} className="books-carousel" style={{
          display: 'flex', gap: 24, overflowX: 'auto', scrollBehavior: 'smooth',
          padding: '8px 0', scrollbarWidth: 'none',
        }}>
          <style>{`.books-carousel::-webkit-scrollbar{display:none}`}</style>
          {SHOWCASE_BOOKS.map((book) => (
            <Link key={book.token} to={`/read/${book.token}`}
              style={{ textDecoration: 'none', textAlign: 'center', flexShrink: 0 }}>
              <img src={PROD_BASE + book.cover} alt={book.title} style={{
                width: 150, height: 220, objectFit: 'cover', borderRadius: 8,
                boxShadow: '0 4px 16px rgba(0,0,0,0.4)', transition: 'transform 0.2s',
              }}
                onMouseOver={e => (e.currentTarget.style.transform = 'scale(1.05)')}
                onMouseOut={e => (e.currentTarget.style.transform = 'scale(1)')}
              />
              <div style={{ marginTop: 10, fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', maxWidth: 150 }}>
                {book.title}
              </div>
            </Link>
          ))}
        </div>
        <button onClick={() => scroll(1)} aria-label="Next" style={{ ...arrowStyle, right: -16 }}>{'›'}</button>
      </div>
    </section>
  );
};

/* ── How It Works ── */
export const HowItWorks: React.FC = () => {
  const steps = [
    { num: '1', icon: '💡', title: 'Describe Your Book', desc: "Pick a genre and write one sentence about your idea. That's all we need." },
    { num: '2', icon: '🤖', title: 'AI Writes Your Book', desc: 'Full novel with chapters, cover, and audiobook generated in minutes.' },
    { num: '3', icon: '💰', title: 'Publish and Earn', desc: 'Export to Amazon KDP with one click. Start earning royalties today.' },
  ];

  return (
    <section style={{ padding: '64px 24px', background: 'var(--ground)' }}>
      <h2 style={{
        textAlign: 'center', fontSize: 'clamp(22px, 4vw, 34px)', fontWeight: 700,
        marginBottom: 48, color: 'var(--text-primary)'
      }}>
        From Idea to Amazon in 3 Steps
      </h2>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: 32, maxWidth: 960, margin: '0 auto'
      }}>
        {steps.map((s) => (
          <div key={s.num} style={{ textAlign: 'center' as const, padding: 24 }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%',
              background: 'linear-gradient(135deg, var(--accent), var(--accent-bg))',
              color: 'var(--text-on-accent)', fontSize: 28, fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              {s.icon}
            </div>
            <h3 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8, color: 'var(--text-primary)' }}>{s.title}</h3>
            <p style={{ fontSize: 15, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{s.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

/* ── AI Humanizer Section ── */
export const AIHumanizerSection: React.FC = () => {
  const detectors = ['Turnitin', 'GPTZero', 'Originality.ai', 'Copyleaks', 'Writer'];

  return (
    <section style={{
      padding: '64px 24px',
      background: 'linear-gradient(180deg, #1a1412 0%, #221a16 100%)',
    }}>
      <div style={{ maxWidth: 800, margin: '0 auto', textAlign: 'center' as const }}>
        {/* Badge */}
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          background: 'linear-gradient(135deg, rgba(212,165,116,0.15), rgba(212,165,116,0.05))',
          border: '1px solid rgba(212,165,116,0.3)',
          borderRadius: '24px',
          padding: '8px 20px',
          marginBottom: '24px',
          fontSize: '14px',
          fontWeight: 600,
          color: 'var(--accent)',
          letterSpacing: '0.5px',
        }}>
          🛡️ 99% Human Written
        </div>

        {/* Headline */}
        <h2 style={{
          fontSize: 'clamp(24px, 4vw, 36px)',
          fontWeight: 700,
          color: 'var(--text-primary)',
          marginBottom: '16px',
          lineHeight: 1.2,
        }}>
          AI-Generated Books That Sound{' '}
          <span style={{ color: 'var(--accent)' }}>100% Human</span>
        </h2>

        {/* Subtext */}
        <p style={{
          fontSize: '17px',
          color: 'var(--text-secondary)',
          lineHeight: 1.7,
          marginBottom: '32px',
          maxWidth: 600,
          margin: '0 auto 32px',
        }}>
          Our AI Humanizer transforms robotic text into smooth, natural writing
          that produces natural, human-quality prose.
        </p>

        {/* Detector logos */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap' as const,
          justifyContent: 'center',
          gap: '16px',
          marginBottom: '36px',
        }}>
          {detectors.map((name) => (
            <div key={name} style={{
              background: 'var(--surface)',
              border: '1px solid rgba(212,165,116,0.12)',
              borderRadius: '12px',
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: 500,
              color: 'var(--text-secondary)',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}>
              <span style={{ color: 'var(--t-4ade80)', fontSize: '16px' }}>✓</span>
              {name}
            </div>
          ))}
        </div>

        {/* CTA */}
        <a
          href="/humanize"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: 'linear-gradient(135deg, var(--accent), var(--accent-bg))',
            color: 'var(--text-on-accent)',
            padding: '14px 32px',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: 700,
            textDecoration: 'none',
            transition: 'transform 0.2s, box-shadow 0.2s',
            boxShadow: '0 4px 16px rgba(212,165,116,0.3)',
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 24px rgba(212,165,116,0.4)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 16px rgba(212,165,116,0.3)';
          }}
        >
          🛡️ Try AI Humanizer Free
        </a>
      </div>
    </section>
  );
};
